// note-similarity.ts — lightweight, explainable note-body comparison for the
// Labyrinth's Thread flow. Three layers, all pure + client-side:
//   • TF-IDF + cosine (topSimilarUnlinkedPairs) — surface similar-but-unlinked
//     pairs so the user can connect related ideas ("ball of yarn").
//   • exact-duplicate grouping (duplicateContentGroups) — same body, different
//     title → offer a lossless merge.
//   • near-duplicate scoring + diff (bodySimilarity / lineDiff / unionMerge) —
//     two notes that are the same note lightly evolved → offer a merge that shows
//     what differs and can combine both losslessly.
//
// Deliberately NO embeddings / RAG — fully client-side, fast, and EXPLAINABLE.
// Pure leaf: imports nothing from notes/editor (only a type).

import { isScheduledLine } from '../utils/search-predicate.js';
import type { NoteRecord } from './note-graph-index.js';

// A compact English stopword list — enough to strip the highest-frequency glue
// words that would otherwise dominate similarity without adding meaning.
const STOPWORDS = new Set(
  (
    'the a an and or but if then else when while for to of in on at by with from up out off over ' +
    'under again further once here there all any both each few more most other some such no nor not ' +
    'only own same so than too very can will just dont should now is are was were be been being have ' +
    'has had do does did this that these those it its it’s i you he she we they them his her our your ' +
    'their what which who whom about into through during before after above below between as because ' +
    'until against among also however therefore thus etc via per yet still much many lot get got make ' +
    'made like also one two three new use used using would could may might must shall need want see ' +
    'note notes day today'
  ).split(' ')
);

export interface SimilarPair {
  a: string;
  b: string;
  /** cosine similarity in 0..1. */
  score: number;
  /** top shared high-weight terms — the explainable "why". */
  sharedTerms: string[];
}

export interface SimilarityOptions {
  limit?: number;
  /** pairKey()s to skip (declined suggestions). */
  declined?: Set<string>;
  /** minimum cosine score to surface (default 0.06). */
  minScore?: number;
}

/** Order-independent key for a note pair (for declined-tracking + dedup). */
export function pairKey(a: string, b: string): string {
  return a < b ? `${a}\0${b}` : `${b}\0${a}`;
}

/** Lowercase alpha tokens (length ≥ 3), markdown-link noise stripped, stopworded. */
export function tokenize(text: string): string[] {
  // Drop the title heading + scheduled/calendar lines so a shared title or a
  // single shared calendar event doesn't drive the similarity.
  const body = text
    .split('\n')
    .filter((line, i) => !(i === 0 && /^#{1,6}\s/.test(line)) && !isScheduledLine(line))
    .join('\n');
  const cleaned = body
    .replace(/\[\[([^\]]+)\]\]/g, ' $1 ') // keep wiki-link inner text, drop brackets
    .replace(/\]\([^)]*\)/g, '] ') // drop markdown link targets
    .replace(/https?:\/\/\S+/g, ' ') // drop bare URLs
    .replace(/`+/g, ' ')
    .toLowerCase();
  const out: string[] = [];
  const re = /[a-z][a-z'-]{2,}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(cleaned)) !== null) {
    const w = m[0].replace(/^[-']+|[-']+$/g, '');
    if (w.length >= 3 && !STOPWORDS.has(w)) out.push(w);
  }
  return out;
}

// Skip terms that appear in more than this many docs when generating candidate
// pairs — they're low-IDF and would blow up the O(df^2) pair generation.
const DF_CAP = 60;

/**
 * The strongest similar-but-NOT-already-linked note pairs, each with the top
 * shared terms that explain the similarity. Deterministic. Candidate pairs are
 * generated from a term→docs inverted index (only notes that share a term are
 * compared), so this stays tractable on large vaults.
 */
export function topSimilarUnlinkedPairs(
  notes: NoteRecord[],
  linkMap: Map<string, Set<string>>,
  opts: SimilarityOptions = {}
): SimilarPair[] {
  const limit = opts.limit ?? 12;
  const minScore = opts.minScore ?? 0.06;
  const declined = opts.declined ?? new Set<string>();

  // Term frequencies per doc + document frequency across the corpus.
  const docTerms: Array<Map<string, number>> = [];
  const docLen: number[] = [];
  const names: string[] = [];
  const docFreq = new Map<string, number>();
  for (const { name, content } of notes) {
    const tokens = tokenize(content);
    if (!tokens.length) continue;
    const tf = new Map<string, number>();
    for (const t of tokens) tf.set(t, (tf.get(t) ?? 0) + 1);
    for (const t of tf.keys()) docFreq.set(t, (docFreq.get(t) ?? 0) + 1);
    docTerms.push(tf);
    docLen.push(tokens.length);
    names.push(name);
  }
  const N = docTerms.length;
  if (N < 2) return [];

  // L2-normalized TF-IDF vectors.
  const vectors: Array<Map<string, number>> = [];
  for (const tf of docTerms) {
    const vec = new Map<string, number>();
    let norm = 0;
    for (const [t, count] of tf) {
      const idf = Math.log(N / (docFreq.get(t) ?? 1));
      if (idf <= 0) continue; // term in every doc → no signal
      const w = count * idf;
      vec.set(t, w);
      norm += w * w;
    }
    norm = Math.sqrt(norm) || 1;
    for (const [t, w] of vec) vec.set(t, w / norm);
    vectors.push(vec);
  }

  // Inverted index term → doc indices (skip too-common + singleton terms).
  const termDocs = new Map<string, number[]>();
  for (let i = 0; i < vectors.length; i++) {
    for (const t of vectors[i].keys()) {
      let arr = termDocs.get(t);
      if (!arr) {
        arr = [];
        termDocs.set(t, arr);
      }
      arr.push(i);
    }
  }

  const linked = (a: string, b: string): boolean =>
    Boolean(linkMap.get(a)?.has(b)) || Boolean(linkMap.get(b)?.has(a));

  // Generate candidate pairs from co-occurring terms.
  const candidates = new Set<number>(); // encoded i*N + j (i<j)
  for (const docs of termDocs.values()) {
    if (docs.length < 2 || docs.length > DF_CAP) continue;
    for (let x = 0; x < docs.length; x++) {
      for (let y = x + 1; y < docs.length; y++) {
        const i = docs[x];
        const j = docs[y];
        candidates.add(i * N + j);
      }
    }
  }

  const results: Array<SimilarPair & { rank: number }> = [];
  for (const enc of candidates) {
    const i = Math.floor(enc / N);
    const j = enc % N;
    const a = names[i];
    const b = names[j];
    if (linked(a, b)) continue;
    if (declined.has(pairKey(a, b))) continue;

    const va = vectors[i];
    const vb = vectors[j];
    // Cosine = dot product (vectors are already normalized). Iterate the smaller.
    const [small, large] = va.size <= vb.size ? [va, vb] : [vb, va];
    let dot = 0;
    const shared: Array<[string, number]> = [];
    for (const [t, w] of small) {
      const w2 = large.get(t);
      if (w2 === undefined) continue;
      dot += w * w2;
      shared.push([t, w * w2]);
    }
    if (dot < minScore) continue;
    shared.sort((p, q) => q[1] - p[1]);
    // Weight by note length so two substantive notes outrank two short notes
    // that share only a calendar event or a similar title. Saturating: a note
    // with ~25 meaningful tokens reaches half weight, longer notes approach 1.
    const minLen = Math.min(docLen[i], docLen[j]);
    const lengthWeight = minLen / (minLen + 25);
    results.push({
      a,
      b,
      score: dot,
      sharedTerms: shared.slice(0, 4).map(([t]) => t),
      rank: dot * lengthWeight,
    });
  }

  results.sort((p, q) => q.rank - p.rank || (pairKey(p.a, p.b) < pairKey(q.a, q.b) ? -1 : 1));
  return results.slice(0, limit).map(({ rank, ...p }) => p);
}

export interface DuplicateGroup {
  /** ≥2 distinct note names whose title-stripped bodies are byte-identical, sorted asc. */
  names: string[];
}

export interface DuplicateOptions {
  /** names to skip entirely (reserved + daily/deny-listed) — injected by the caller so
   *  this leaf stays free of notes/ imports. */
  isExcluded?: (name: string) => boolean;
  /** minimum meaningful-token count for a body to be eligible (default 2). Stops empty
   *  stubs / title-only notes from all collapsing into one mega-group. */
  minBodyTokens?: number;
}

/** Strip a single leading heading line (`# …` / `###### …`) and trailing whitespace —
 *  the body comparison key. Mirrors replaceFirst()'s H1-skip in labyrinth-view so
 *  detection and link-insertion agree on what "the title" is. */
function normalizeBody(content: string): string {
  const nl = content.indexOf('\n');
  const hasHeading = nl !== -1 && /^#{1,6}\s/.test(content);
  return (hasHeading ? content.slice(nl + 1) : content).trim();
}

/**
 * Groups of notes that have the SAME body but DIFFERENT titles — i.e. accidental
 * duplicates the Labyrinth's Thread flow can offer to merge. Two notes match when
 * their title-stripped, trimmed bodies are byte-identical. Names within a group and
 * the groups themselves are sorted for deterministic ordering. Empty / trivial /
 * excluded notes never participate.
 */
export function duplicateContentGroups(
  notes: NoteRecord[],
  opts: DuplicateOptions = {}
): DuplicateGroup[] {
  const minBodyTokens = opts.minBodyTokens ?? 2;
  const byBody = new Map<string, string[]>();
  for (const { name, content } of notes) {
    if (opts.isExcluded?.(name)) continue;
    const body = normalizeBody(content);
    if (!body) continue; // empty body → not a meaningful duplicate
    if (tokenize(content).length < minBodyTokens) continue; // trivial / title-only guard
    let arr = byBody.get(body);
    if (!arr) {
      arr = [];
      byBody.set(body, arr);
    }
    arr.push(name);
  }
  const groups: DuplicateGroup[] = [];
  for (const names of byBody.values()) {
    if (names.length < 2) continue;
    names.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    groups.push({ names });
  }
  groups.sort((a, b) => (a.names[0] < b.names[0] ? -1 : a.names[0] > b.names[0] ? 1 : 0));
  return groups;
}

// ── Near-duplicate detection (char similarity) + line diff/union ─────────────
// Exact duplicates are rare; the common case is two notes that are the SAME note
// lightly evolved (a changed date, an added line). bodySimilarity scores how close
// two bodies are, lineDiff explains the difference, and unionMerge combines them
// losslessly — together they let the Labyrinth offer a "merge similar notes" flow.

/** Collapse all whitespace runs to single spaces and trim — the char-similarity key. */
function collapseWs(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/** Levenshtein edit distance (two-row DP). */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array<number>(n + 1);
  let cur = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    cur[0] = i;
    const ai = a.charCodeAt(i - 1);
    for (let j = 1; j <= n; j++) {
      const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    const tmp = prev;
    prev = cur;
    cur = tmp;
  }
  return prev[n];
}

// Char-diff is O(n·m); above this normalized length we bail to exact-equality only
// (a near-dup of two huge notes is implausible and not worth the quadratic cost).
const SIMILARITY_LENGTH_CAP = 4000;

/**
 * How close two note BODIES are, in 0..1 — 1 = identical (ignoring the title line
 * and insignificant whitespace), lower = more divergent. A normalized
 * char-level (Levenshtein) ratio: robust to a swapped date or an added word, and
 * cleanly separates near-duplicates (~0.9+) from merely-topical notes (~0.1).
 */
export function bodySimilarity(a: string, b: string): number {
  const na = collapseWs(normalizeBody(a));
  const nb = collapseWs(normalizeBody(b));
  const maxLen = Math.max(na.length, nb.length);
  if (maxLen === 0) return 1;
  if (maxLen > SIMILARITY_LENGTH_CAP) return na === nb ? 1 : 0;
  return 1 - levenshtein(na, nb) / maxLen;
}

export interface LineDiff {
  /** every line of both bodies in merged order — `t` says which side(s) it's on. */
  ops: Array<{ t: '=' | 'a' | 'b'; line: string }>;
  /** non-blank lines present only in `a`. */
  aOnly: string[];
  /** non-blank lines present only in `b`. */
  bOnly: string[];
}

/**
 * Line-level diff of two note bodies (title line stripped), via longest-common-
 * subsequence. `ops` is the lines of both in a single merged order (the lossless
 * union); `aOnly` / `bOnly` are the non-blank lines unique to each side — the
 * human-readable "what differs".
 */
export function lineDiff(a: string, b: string): LineDiff {
  const al = normalizeBody(a).split('\n');
  const bl = normalizeBody(b).split('\n');
  const m = al.length;
  const n = bl.length;
  // LCS lengths table.
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = al[i] === bl[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops: LineDiff['ops'] = [];
  const aOnly: string[] = [];
  const bOnly: string[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (al[i] === bl[j]) {
      ops.push({ t: '=', line: al[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ t: 'a', line: al[i] });
      if (al[i].trim()) aOnly.push(al[i]);
      i++;
    } else {
      ops.push({ t: 'b', line: bl[j] });
      if (bl[j].trim()) bOnly.push(bl[j]);
      j++;
    }
  }
  while (i < m) {
    ops.push({ t: 'a', line: al[i] });
    if (al[i].trim()) aOnly.push(al[i]);
    i++;
  }
  while (j < n) {
    ops.push({ t: 'b', line: bl[j] });
    if (bl[j].trim()) bOnly.push(bl[j]);
    j++;
  }
  return { ops, aOnly, bOnly };
}

/**
 * Losslessly combine two note bodies (title line stripped): the LCS backbone with
 * every unique line from BOTH sides woven in at its diff position. `a`'s structure
 * leads, so pass the survivor's body first.
 */
export function unionMerge(a: string, b: string): string {
  return lineDiff(a, b)
    .ops.map((o) => o.line)
    .join('\n');
}
